import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export interface SearchResult {
  id: string;
  code: string;
  type: string;
  title: string;
  subtitle?: string;
  url: string;
  matchedField?: string;
}

export interface SearchResponse {
  query: string;
  totalResults: number;
  results: Record<string, SearchResult[]>;
  counts: Record<string, number>;
}

const RECENT_KEY = "phazex_recent_searches";
const RECENT_MAX = 5;

export function readRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr)
      ? arr.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX)
      : [];
  } catch {
    return [];
  }
}

function writeRecentSearches(list: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch {
    // ignore storage errors
  }
}

/** Recent-searches state synced to localStorage; newest first, max 5, deduped. */
export function useRecentSearches() {
  const [recent, setRecent] = useState<string[]>(() => readRecentSearches());

  const add = useCallback((q: string) => {
    const term = q.trim();
    if (term.length < 2) return;
    setRecent((prev) => {
      const next = [term, ...prev.filter((x) => x.toLowerCase() !== term.toLowerCase())].slice(
        0,
        RECENT_MAX,
      );
      writeRecentSearches(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    writeRecentSearches([]);
    setRecent([]);
  }, []);

  return { recent, add, clear };
}

/** Debounce any value by `delay` ms. */
export function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * React Query search. Disabled until query >= 2 chars. React Query cancels the
 * stale request automatically when the key changes (rapid typing → no races).
 */
export function useSearchQuery(query: string, modules: string[]) {
  const { accessToken } = useAuth();
  const q = query.trim();
  const enabled = !!accessToken && q.length >= 2;

  return useQuery<SearchResponse>({
    queryKey: ["search", q, [...modules].sort(), accessToken ? "auth" : "anon"],
    enabled,
    staleTime: 30_000,
    queryFn: () =>
      apiFetch<SearchResponse>("/search", {
        token: accessToken,
        query: {
          q,
          modules: modules.length > 0 ? modules.join(",") : undefined,
        },
      }),
  });
}
